import React, { useState, useEffect, useContext } from "react";
import { createPortal } from "react-dom";
import {
  RemixBuilderProvider,
  RemixBuilderContext,
} from "./shared/RemixBuilderContext";
import CtaRemixBuilder from "./CtaRemixBuilder";
import VariantRemixBuilder from "./VariantRemixBuilder";
import RemixModalOverlay from "./shared/RemixModalOverlay";
import useRemix from "./shared/useRemix";

const EVENT_NAME = "openCustomTemplateModal";

const CustomTemplateModalInner = ({ onClose, type = "cta" }) => {
  const { Remix } = useRemix({ builder: true });
  const [contextData, builder, setContextData] = useContext(RemixBuilderContext);

  useEffect(() => {
    const fetchTemplates = async () => {
      const templates = await Remix.getSectionTemplates();
      setContextData((prev) => ({ ...prev, section_templates: templates }));
    };
    fetchTemplates();
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="cf-remix-builder-wrapper-modal remix-editor modal fade in"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      data-backdrop="false"
      style={{
        display: "block",
        background: "rgba(0, 0, 0, 0.5)",
        overflowY: "auto",
        zIndex: 10001,
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="modal-dialog !w-[100%] !max-w-[1600px] !h-[100%] ml-auto mr-auto"
        role="document"
      >
        <div className="modal-content hard-center bg-white dark:bg-slate-950 !shadow-2xl max-h-[95vh] flex flex-column">
          <div className="modal-header !border-slate-200 dark:!border-slate-800 relative z-[12000]">
            <button
              type="button"
              className="close dark:text-white relative z-[12000] pointer-events-auto"
              onClick={onClose}
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 17 17"
                fill="none"
              >
                <path
                  d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div className="modal-body flex h-[85vh] relative">
            {contextData.remix_loading?.loading && (
              <div
                className="absolute inset-x-0 bottom-0 -top-[35px] z-20 flex items-center justify-center bg-white dark:bg-slate-950"
                aria-hidden="false"
              >
                <RemixModalOverlay remixLoading={contextData.remix_loading} />
              </div>
            )}
            <div className="flex gap-5 mt-[-35px] px-7 flex-1 min-w-0">
              {type === "variant" ? (
                <VariantRemixBuilder rightColumnClassName="pt-[30px]" />
              ) : (
                <CtaRemixBuilder rightColumnClassName="pt-[30px]" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomTemplateModalWrapper = (props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [templateData, setTemplateData] = useState(null);
  const [mountKey, setMountKey] = useState(0);

  const {
    website,
    cta = null,
    subscription,
    account,
    current_user,
    remix_mappings,
    styles = {},
    environment,
    FILESTACK_API_KEY,
  } = props;

  const type = cta ? "variant" : "cta";

  useEffect(() => {
    const handleOpen = (event) => {
      const { sectionTemplates, funnelSteps, remixPrompt, ctaType, detectedAssetTheme } =
        event.detail || {};
      const validSectionTemplates = Array.isArray(sectionTemplates)
        ? sectionTemplates.filter(
            (t) =>
              t &&
              typeof t === "object" &&
              ((t.id != null && t.id !== "") ||
                (t.tag_name != null && String(t.tag_name).trim() !== ""))
          )
        : [];
      const validFunnelSteps = Array.isArray(funnelSteps)
        ? funnelSteps
            .map((step, index) => {
              const stepTemplates = Array.isArray(step?.section_templates)
                ? step.section_templates.filter(
                    (t) =>
                      t &&
                      typeof t === "object" &&
                      ((t.id != null && t.id !== "") ||
                        (t.tag_name != null && String(t.tag_name).trim() !== ""))
                  )
                : [];
              return {
                // Normalize preload to sequential positions to avoid sparse/shifted tabs.
                step_position: index + 1,
                step_name: step?.step_name || "",
                section_templates: stepTemplates,
              };
            })
            .filter(Boolean)
        : [];

      if (validSectionTemplates.length === 0 && validFunnelSteps.length === 0) return;

      const urlMatch = window.location.pathname.match(/\/websites\/(\d+)/);
      const urlWebsiteId = urlMatch ? parseInt(urlMatch[1], 10) : null;
      if (urlWebsiteId && website?.id && urlWebsiteId !== website.id) return;

      // Prevent other CustomTemplateModalWrapper instances (e.g. from partial + page) from opening
      event.stopImmediatePropagation();

      setTemplateData({
        sectionTemplates: validSectionTemplates,
        funnelSteps: validFunnelSteps,
        remixPrompt,
        ctaType,
        detectedAssetTheme: detectedAssetTheme || null,
      });
      setMountKey((k) => k + 1);
      setModalOpen(true);
      document.body.classList.add("modal-open");
    };

    document.addEventListener(EVENT_NAME, handleOpen);
    return () => document.removeEventListener(EVENT_NAME, handleOpen);
  }, []);

  useEffect(() => {
    if (!modalOpen) return;
    const handleEscape = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [modalOpen]);

  const handleClose = () => {
    setModalOpen(false);
    setTemplateData(null);
    const otherModalsOpen =
      document.querySelector(".playbook-chat-modal:not(.hidden)") ||
      document.querySelector(
        ".playbook-chat-embed-wrapper.playbook-chat-modal-active"
      );
    if (!otherModalsOpen) {
      document.body.classList.remove("modal-open");
    }
  };

  if (!modalOpen || !templateData) return null;

  const modalContent = (
    <RemixBuilderProvider
      key={mountKey}
      type={type}
      website={website}
      cta={cta}
      subscription={subscription}
      account={account}
      current_user={current_user}
      environment={environment}
      FILESTACK_API_KEY={FILESTACK_API_KEY}
      remix_mappings={remix_mappings}
      styles={
        templateData.detectedAssetTheme
          ? { ...styles, ...templateData.detectedAssetTheme }
          : styles
      }
      initialSectionTemplates={templateData.sectionTemplates}
      initialFunnelSteps={templateData.funnelSteps}
      initialRemixPrompt={templateData.remixPrompt || ""}
      initialCtaType={templateData.ctaType}
      detectedAssetTheme={templateData.detectedAssetTheme}
    >
      <CustomTemplateModalInner onClose={handleClose} type={type} />
    </RemixBuilderProvider>
  );

  return createPortal(modalContent, document.body);
};

export default CustomTemplateModalWrapper;
export { EVENT_NAME };
